import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { num } from '../../format';
import { useNavigation } from '../layout/navigation';
import { componentBomUsage, type BomUsage } from './api';

/** Read-only reverse BOM view. Alternatives deliberately remain separate from
 * primary rows: an allowed substitute is not a part the plan has committed to. */
export function BomUsagePanel({ componentId }: { componentId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [projectsOnly, setProjectsOnly] = useState(false);
  const usage = useQuery({
    queryKey: ['bom-usage', componentId, statuses, projectsOnly],
    queryFn: () => componentBomUsage(componentId, { statuses, activeProjectsOnly: projectsOnly }),
    enabled: open,
  });
  const toggle = (status: string) => setStatuses((old) => old.includes(status) ? old.filter((item) => item !== status) : [...old, status]);

  return (
    <section className="panel__section">
      <button type="button" className="panel__section-head" onClick={() => setOpen((value) => !value)}>
        {t('components.whereUsed')}
      </button>
      {open && (
        <div className="bom-analysis">
          <div className="bom-analysis__filters">
            {(['draft', 'released', 'deprecated'] as const).map((status) => (
              <label key={status}><input type="checkbox" checked={statuses.includes(status)} onChange={() => toggle(status)} /> {t(`specs.status.${status}`)}</label>
            ))}
            <label><input type="checkbox" checked={projectsOnly} onChange={(event) => setProjectsOnly(event.target.checked)} /> {t('components.activeProjectsOnly')}</label>
          </div>
          {usage.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
          {usage.data?.length === 0 && <p className="panel__hint">{t('components.noUsage')}</p>}
          {usage.data?.map((row, index) => <Usage key={`${row.version_id}-${row.kind}-${index}`} row={row} />)}
        </div>
      )}
    </section>
  );
}

function Usage({ row }: { row: BomUsage }) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  return (
    <article className={`bom-analysis__row bom-analysis__row--${row.kind}`}>
      <strong>
        <button
          type="button"
          className="link"
          onClick={() => navigation.open('spec', row.specification_id)}
        >
          {row.specification_name} {t('specs.versionShort', { version: row.version })}
        </button>
      </strong>{' '}
      <span className={`spec__badge spec__badge--${row.status}`}>
        {t(`specs.status.${row.status}`)}
      </span>
      <p>{row.kind === 'primary' ? (row.direct ? t('components.directUse') : t('components.nestedUse')) : t('components.alternativeUse')}</p>
      <dl className="panel__facts">
        <dt>{t('components.designators')}</dt><dd>{row.reference_designators || '—'}</dd>
        <dt>{t('components.perProduct')}</dt><dd>{num(row.quantity_per_product)} ({row.calculation}){row.is_optional && ` · ${t('specs.optionalShort')}`}</dd>
        <dt>{t('components.path')}</dt><dd>{row.path.join(' → ')}</dd>
      </dl>
      {row.kind === 'alternative' && <p className="panel__hint">{t('components.alternativeHint')}</p>}
      {row.projects.length > 0 && (
        <ul>
          {row.projects.map((project) => (
            <li key={project.project_id}>
              <button
                type="button"
                className="link"
                onClick={() => navigation.open('project', project.project_id)}
              >
                {project.code} — {project.name}: {project.units} {t('projects.units')} (
                {t('specs.versionShort', { version: project.pinned_version })})
              </button>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
